import type { BetSlipItem, EventDetails, MultiInvestment } from '../types'
import type { BetSlipBetsState } from '../Store/Bets'
import {
  isToteSelection,
  isFobDetails,
  isRaceDetails,
  isStartingPriceSelection,
  isSameRaceMultiSelection,
  isAllUpSelection,
} from './typeGuards'
import { MIN_LEGS_IN_MULTI, MAX_LEGS_IN_RACE_MULTI } from '../helpers/constants'

export const isSelectionAllowedInMulti = (selection: BetSlipItem['selection']): boolean => {
  return (
    !isToteSelection(selection) &&
    !isStartingPriceSelection(selection) &&
    !isSameRaceMultiSelection(selection)
  )
}

export const isFatalErrorType = (betErrorType: BetSlipItem['betErrorType']): boolean => {
  if (!betErrorType) return false
  return betErrorType === 'Closed' || betErrorType === 'HandicapChanged'
}

export const isNonHandledError = (betErrorType: BetSlipItem['betErrorType']): boolean =>
  betErrorType !== undefined && betErrorType !== 'PricesChanged'

export const isFatalMultiBetLegError = (item: BetSlipItem): boolean =>
  isFatalErrorType(item.multiBetLegError?.betErrorType)

export const hasErrors = (item: BetSlipItem): boolean => !!item.betErrorType

export const isValidMultiInvestmentForLegs = (
  multiInvestment: MultiInvestment,
  multiItemsCount: number,
  { shouldCheckOnlyFormulas }: { shouldCheckOnlyFormulas?: boolean } = {}
): boolean =>
  Array.from(Array(Math.min(multiItemsCount, 6)).keys()).some(cur => {
    if (cur === 0 && !shouldCheckOnlyFormulas) return !!multiInvestment.value
    if (cur === 1 && !shouldCheckOnlyFormulas) return false
    return !!multiInvestment[`f${cur}` as keyof MultiInvestment]
  })

export const isValidMulti = (
  multiInvestment: MultiInvestment,
  multiError: BetSlipBetsState['multiBetError'] | null,
  multiItems: BetSlipItem[]
): boolean => {
  const hasValidInvestment = isValidMultiInvestmentForLegs(multiInvestment, multiItems.length)

  return (
    hasValidInvestment &&
    !hasTooFewMultiLegs(multiItems) &&
    !hasTooManyMultiLegs(multiItems) &&
    !hasInvalidLegsOnMulti(multiItems) &&
    !multiError?.betErrorType
  )
}

export const hasNoFatalErrors = (item: BetSlipItem) => !isFatalErrorType(item.betErrorType)

export const hasBeenPlaced = (item: BetSlipItem) => !!item.receipt

export const hasWinBoostedSuperPick = (item: BetSlipItem) =>
  item.specialOffers.some(offer => offer.isBoostOffer && offer.legTypeCode === 'W')

export const hasPlaceBoostedSuperPick = (item: BetSlipItem) =>
  item.specialOffers.some(offer => offer.isBoostOffer && offer.legTypeCode === 'P')

export const hasInvestment = (item: BetSlipItem) =>
  (!!item.investment.win && item.investment.win.value > 0) ||
  (!!item.investment.place && item.investment.place.value > 0)

export const hasTooFewMultiLegs = (multiItems: BetSlipItem[]) =>
  multiItems.filter(item => item.isInMulti).length < MIN_LEGS_IN_MULTI

export const hasTooManyMultiLegs = (multiItems: BetSlipItem[]) => {
  return multiItems.filter(item => item.isInMulti).length > MAX_LEGS_IN_RACE_MULTI
}

export const getBetsToPlace = (items: BetSlipItem[]): BetSlipItem[] => {
  return items.flatMap(item => {
    if (!hasNoUnspecifiedErrors(item)) return []
    if (item.betErrorType === 'DuplicateBonusBet') return []
    if (item.betErrorType === 'BetPlacementFault') return []
    if (!hasNoFatalErrors(item)) return []
    if (hasBeenPlaced(item)) return []
    if (!hasInvestment(item)) return []

    return [item]
  })
}

export const getBetsInMulti = (items: BetSlipItem[]): BetSlipItem[] =>
  items.filter(item => item.isInMulti)

export const isSpecialUsed = (item: BetSlipItem, items: BetSlipItem[]): boolean => {
  if (Array.isArray(item.specialOffers) && item.specialOffers.length > 0) {
    return items.some(
      bet =>
        !!bet.selectedSuperPickOffer &&
        bet.selectedSuperPickOffer.tokenId === item.specialOffers[0].tokenId &&
        bet.id !== item.id
    )
  }
  return false
}

export const hasEnoughItemsToCreateValidMulti = (items: BetSlipItem[]) => {
  const uniqueKeys = new Set<string>()

  const validMultiItems = items.filter(item => {
    const key = getMultiLegEventOrRaceKey(item.selectionDetails)
    if (uniqueKeys.has(key)) {
      return false
    } else {
      uniqueKeys.add(key)
      return isSelectionAllowedInMulti(item.selection)
    }
  })
  return validMultiItems.length >= MIN_LEGS_IN_MULTI
}

export function getMultiLegEventOrRaceKey(selectionDetails: EventDetails) {
  if (isRaceDetails(selectionDetails)) {
    return selectionDetails.races[0].key
  }

  if (isFobDetails(selectionDetails)) {
    return selectionDetails.event
  }

  return 'UNHANDLED'
}

export function getSelectionKey(item: BetSlipItem): string {
  const baseKey = getMultiLegEventOrRaceKey(item.selectionDetails)
  const selection = item.selection

  let output = 'NA'

  const betTypeKey = 'betType' in selection ? selection.betType : 'NA'
  const propSeq = 'propositionSeq' in selection ? selection.propositionSeq : 'NA'

  if (isFobDetails(item.selectionDetails)) {
    output = `${baseKey}-${betTypeKey}-${item.selectionDetails.propositionName}`
  }

  if (isRaceDetails(item.selectionDetails)) {
    const acceptorName = item.selectionDetails.acceptors?.[0]?.name ?? 'NA'
    output = `${baseKey}-${betTypeKey}-${acceptorName}-${propSeq}`
  }

  if (isToteSelection(selection) && selection.selectionString) {
    output += `-${selection.selectionString}`
  }

  if (isAllUpSelection(selection)) {
    const allUpDetailsStr = selection.details
      .map(d => `RN-${d.raceNum}-PT-${d.poolType}-BS-${d.betSelections}`)
      .join('-')
    output += `-${allUpDetailsStr}`
  }

  return output
}

export function areItemsInBetSlip(items: BetSlipItem[], itemsToCheck: BetSlipItem[]): boolean {
  const existingKeys = new Set(items.map(item => getSelectionKey(item)))

  const result = itemsToCheck.some(itemToCheck => existingKeys.has(getSelectionKey(itemToCheck)))

  return result
}

// =============
// Local Helpers
// =============

function hasInvalidLegsOnMulti(items: BetSlipItem[]): boolean {
  const uniqueKeys = new Set<string>()
  const selectedMuliItems = getBetsInMulti(items)

  return selectedMuliItems.some((item): boolean => {
    if (!isSelectionAllowedInMulti(item.selection)) return true

    const key = getMultiLegEventOrRaceKey(item.selectionDetails)

    if (uniqueKeys.has(key)) {
      return true
    } else {
      uniqueKeys.add(key)
      return false
    }
  })
}

const hasNoUnspecifiedErrors = (item: BetSlipItem): boolean => item.betErrorType !== 'Unspecified'
